import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { Event } from '@buzzmap/shared';
import { useUserAuthOptional } from './UserAuthContext';

/**
 * CreatorUpdatesContext — registry of in-flight/completed creator event edits.
 *
 * Why this exists: `EventEditPage` unmounts the instant the user navigates back
 * to `ProlocoDashboard`, so it cannot itself report the PUT's outcome once the
 * request settles. This registry lives above both pages (mounted in App.tsx,
 * inside `UserAuthProvider` since it needs `creatorFetch`) so the dashboard can
 * keep polling per-event state after the edit page is gone.
 *
 * `submitUpdate` never rejects — failures are recorded per event id and read
 * back via `getUpdateError`, since the caller (EventEditPage) fires the update
 * and navigates away immediately without awaiting the result.
 */
export interface CreatorUpdatesContextType {
  /** True while a PUT is in flight for this event id. */
  isUpdating: (id: string) => boolean;
  /** Server-confirmed event returned by the last successful PUT for this id, if any. */
  getUpdatedEvent: (id: string) => Event | undefined;
  /** Error message from the last failed PUT for this id, if any. */
  getUpdateError: (id: string) => string | undefined;
  /** Fires PUT /api/creator/events/:id with the given partial payload. */
  submitUpdate: (id: string, payload: Record<string, unknown>) => Promise<void>;
  /** Dismisses a stored error for an id (e.g. once the user acknowledges it). */
  clearUpdateError: (id: string) => void;
}

const CreatorUpdatesContext = createContext<CreatorUpdatesContextType | undefined>(undefined);

/**
 * Internal-only: safe fallback for consumers (e.g. useCreatorEvents) that may
 * render without a CreatorUpdatesProvider in tests. Mirrors useUserAuthOptional.
 */
export function useCreatorUpdatesOptional(): CreatorUpdatesContextType {
  const ctx = useContext(CreatorUpdatesContext);
  if (ctx) return ctx;
  return {
    isUpdating: () => false,
    getUpdatedEvent: () => undefined,
    getUpdateError: () => undefined,
    submitUpdate: async () => {},
    clearUpdateError: () => {},
  };
}

interface CreatorUpdatesProviderProps {
  children: ReactNode;
}

export function CreatorUpdatesProvider({ children }: CreatorUpdatesProviderProps) {
  const { creatorFetch } = useUserAuthOptional();
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [updatedEvents, setUpdatedEvents] = useState<Record<string, Event>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isUpdating = useCallback((id: string) => updatingIds.has(id), [updatingIds]);
  const getUpdatedEvent = useCallback((id: string) => updatedEvents[id], [updatedEvents]);
  const getUpdateError = useCallback((id: string) => errors[id], [errors]);

  const clearUpdateError = useCallback((id: string) => {
    setErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const submitUpdate = useCallback(
    async (id: string, payload: Record<string, unknown>): Promise<void> => {
      setUpdatingIds((prev) => new Set(prev).add(id));
      clearUpdateError(id);

      try {
        const res = await creatorFetch(`/api/creator/events/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || errData.error || 'Errore nel salvataggio');
        }

        const updated = (await res.json()) as Event;
        setUpdatedEvents((prev) => ({ ...prev, [id]: updated }));
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          [id]: err instanceof Error ? err.message : 'Errore nel salvataggio',
        }));
      } finally {
        setUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [creatorFetch, clearUpdateError]
  );

  const value = useMemo<CreatorUpdatesContextType>(
    () => ({
      isUpdating,
      getUpdatedEvent,
      getUpdateError,
      submitUpdate,
      clearUpdateError,
    }),
    [isUpdating, getUpdatedEvent, getUpdateError, submitUpdate, clearUpdateError]
  );

  return <CreatorUpdatesContext.Provider value={value}>{children}</CreatorUpdatesContext.Provider>;
}

/**
 * Hook to access the creator-updates registry.
 * @throws Error if used outside of CreatorUpdatesProvider
 */
export function useCreatorUpdates(): CreatorUpdatesContextType {
  const context = useContext(CreatorUpdatesContext);
  if (context === undefined) {
    throw new Error('useCreatorUpdates must be used within a CreatorUpdatesProvider');
  }
  return context;
}
